import { cancel, confirm, isCancel, multiselect, select } from "@clack/prompts";
import {
  getAgentControllerPermissionModeFieldLabel,
  listAgentControllerPermissionModeOptions,
  parseAgentControllerRef,
  resolveAgentControllerPermissionModeForController,
  resolveNativeAgentControllerPermissionModeForController,
} from "@wakeloop/agent-runtime";
import type { AgentControllerPermissionMode } from "@wakeloop/schemas/types";
import pc from "picocolors";
import { resolveLocalRegistryAgentIdFromAgentControllerRef } from "../agents/controller-identity";
import {
  type AgentTargetState,
  listAgentTargetStates,
} from "../agents/registry";
import {
  buildCommandAuthenticationPromptMessage,
  ensureCommandAuthenticationOrCancel,
  evaluateCommandAuthentication,
} from "../auth/command-auth";
import {
  clearSelectedWakeLoopProfileId,
  getSelectedWakeLoopProfileId,
  setSelectedWakeLoopProfileId,
} from "../config/wakeloop-profile-selection";
import {
  deleteWakeLoopProfile,
  listWakeLoopProfiles,
  primeWakeLoopProfilesSessionCache,
  resolveWakeLoopProfile,
  updateWakeLoopProfile,
  type WakeLoopProfile,
} from "../config/wakeloop-profiles-api";
import { submitRuntimeAgentControllerReport } from "../local-service/reporting/runtime-agent-controller-reporting-submission";
import {
  type AgentProfileSetupResult,
  persistAgentTransportSelection,
  resolveAgentProfileSetup,
} from "../profile/agent-setup-flow";
import {
  assertAgentControllerPermissionModeSupported,
  type NormalizedAgentControllerPermissionModeInput,
  resolveAgentControllerPermissionModeInput,
} from "../profile/controller-permission-mode-input";
import {
  type CreateProfileKind,
  type CreateProfileLocalAgentSelection,
  createProfileByKind,
  resolveCreateProfileKind,
  resolveCreateProfileName,
} from "../profile/create-flow";
import {
  ensureWakeLoopManagedProfileWorkspace,
  setCustomProfileWorkspace,
  setWakeLoopManagedProfileWorkspace,
} from "../profile-workspace/service";
import {
  buildAgentProfileRuntimeData,
  buildAgentProfileRuntimeRows,
  buildAgentProfileStateData,
  buildAgentProfileStateRows,
  resolveAgentProfileRuntimeState,
} from "../runtime/agent-profile-state";
import {
  formatInlineWakeLoopCliCommands,
  formatWakeLoopCliCommand,
} from "../runtime/cli-entry-command";
import { type RuntimeContext, resolveRuntimeContext } from "../runtime/context";
import {
  createProfileActionGuideError,
  createProfileMenuGuideError,
  type ProfileActionGuideExamples,
} from "../runtime/dual-view-guide-catalog";
import { canUseInteractivePrompts } from "../runtime/interaction";
import { formatMessage } from "../runtime/messages";
import { createPresenter } from "../runtime/presenter/index";
import { isProfileKindAllowedInView } from "../runtime/view-profile-guard";
import { updateAgentProfilePrimaryBindingAgentControllerPermissionMode } from "../system/agent-profile-binding-agent-controller-permission-mode";
import { updateAgentProfilePrimaryBindingModelSelection } from "../system/agent-profile-binding-model-selection";
import {
  type AgentProfilePrimaryAgentControllerReport,
  resolveAgentProfilePrimaryAgentControllerReport,
} from "../system/agent-profile-primary-binding";
import { ensureAgentProfileRuntimeBinding } from "../system/ensure-agent-profile-runtime-binding";
import { postLocalParticipationMutationReconcile } from "../system/local-participation-mutation-reconcile";
import { renderInfoCard } from "../ui/info-card";
import {
  promptSearchMultiselect,
  promptSearchSelect,
} from "../ui/search-select";
import { promptTextEntry } from "../ui/text-entry";

const EXIT_PROFILE_SELECTION = "__exit__";
const BACK_UPDATE_FIELD_SELECTION = "__back_update_field__";
type ProfileAction = "delete" | "set" | "show" | "update";
type ProfileCommandAuthAction = "create" | "list" | ProfileAction;
type UpdateFieldSelection =
  | "agent_controller_permission_mode"
  | "agent_controller"
  | "name"
  | "workspace";
type ProfileUpdateCommandExamples = Record<string, never>;
type PromptFlowResult<T> =
  | {
      status: "submitted";
      value: T;
    }
  | {
      status: "back";
    }
  | {
      status: "cancelled";
    };

export async function runProfileMenu(input?: {
  view?: string;
  entryChecksHandled?: boolean;
}) {
  const runtime = await resolveRuntimeContext({ view: input?.view });

  if (!canUseInteractivePrompts(runtime)) {
    throw createProfileMenuGuideError();
  }

  if (!input?.entryChecksHandled) {
    const authReady = await ensureProfileCommandAuthenticationOrCancel({
      action: "list",
      runtime,
    });
    if (!authReady) {
      return;
    }
  }

  while (true) {
    const action = await select({
      message: "Profile action",
      options: [
        { value: "create", label: "Create profile" },
        { value: "set", label: "Choose profile" },
        { value: "update", label: "Update profile" },
        { value: "delete", label: "Delete profile" },
        { value: "exit", label: "Exit" },
      ],
    });

    if (isCancel(action) || action === "exit") {
      cancel("Cancelled.");
      return;
    }

    if (
      !(
        action === "create" ||
        action === "set" ||
        action === "update" ||
        action === "delete"
      )
    ) {
      cancel("Cancelled.");
      return;
    }

    if (action === "create") {
      await runProfileCreate({
        view: input?.view,
        entryChecksHandled: true,
      });
      return;
    }

    if (action === "set") {
      await runProfileSet({
        view: input?.view,
        entryChecksHandled: true,
      });
      return;
    }

    if (action === "update") {
      const result = await runProfileUpdate({
        view: input?.view,
        entryChecksHandled: true,
        allowBackToCaller: true,
      });
      if (result === "back") {
        continue;
      }
      return;
    }

    await runProfileDelete({
      view: input?.view,
      entryChecksHandled: true,
    });
    return;
  }
}

export async function runProfileList(input?: {
  view?: string;
  entryChecksHandled?: boolean;
}) {
  const runtime = await resolveRuntimeContext({ view: input?.view });
  if (!input?.entryChecksHandled) {
    const authReady = await ensureProfileCommandAuthenticationOrCancel({
      action: "list",
      runtime,
    });
    if (!authReady) {
      return;
    }
  }
  const presenter = createPresenter(runtime);

  const profiles = await listVisibleProfilesForListByView(runtime.resolvedView);
  if (profiles.length === 0) {
    presenter.line({
      code: "profile.no_profiles",
      text: formatMessage(runtime.resolvedView, "profile.no_profiles"),
    });
    return;
  }

  const current = await resolveSelectedProfileIdForList(profiles);
  for (const profile of profiles) {
    const selected = profile.wakeLoopProfileId === current;
    presenter.line({
      code: "profile.item",
      text: formatProfileListLine({
        profile,
        selected,
        resolvedView: runtime.resolvedView,
      }),
      data: buildProfileListData({
        profile,
        selected,
      }),
    });
  }
}

export async function runProfileShow(input: {
  profile?: string;
  json?: boolean;
  view?: string;
  entryChecksHandled?: boolean;
}) {
  const runtime = await resolveRuntimeContext({ view: input.view });
  if (!input.entryChecksHandled) {
    const authReady = await ensureProfileCommandAuthenticationOrCancel({
      action: "show",
      runtime,
    });
    if (!authReady) {
      return;
    }
  }
  const presenter = createPresenter(runtime);
  const allowPrompt = canUseInteractivePrompts(runtime);
  const explicitProfileId = normalizeOptionalString(input.profile);
  const visibleProfiles = explicitProfileId
    ? []
    : await listVisibleProfilesForListByView(runtime.resolvedView);
  const current = explicitProfileId
    ? null
    : await resolveCurrentProfileId({
        profiles: visibleProfiles,
        mutateSelection: false,
      });
  const profileId =
    explicitProfileId ??
    current ??
    (await resolveTargetProfile({
      explicitProfile: undefined,
      action: "show",
      allowPrompt,
      resolvedView: runtime.resolvedView,
      cachedProfiles: visibleProfiles,
      guideExamples: await resolveProfileUpdateCommandExamples(),
    }));
  if (!profileId) {
    return;
  }

  const profile = await resolveWakeLoopProfile({
    wakeLoopProfileId: profileId,
  });
  assertProfileAllowedInView({
    profileId: profile.wakeLoopProfileId,
    profileKind: profile.profileKind,
    resolvedView: runtime.resolvedView,
    action: "show",
  });
  const agentRuntimeState = await resolveAgentProfileRuntimeState(profile, {
    includeDaemonRouteObservation: true,
  });
  if (input.json === true) {
    const primaryBinding = await resolveProfileShowPrimaryBinding(profile);
    console.log(
      `${JSON.stringify(
        buildProfileShowJsonPayload({
          agentRuntimeState,
          primaryBinding,
          profile,
        }),
        null,
        2
      )}`
    );
    return;
  }

  if (runtime.resolvedView === "human") {
    renderProfileSummaryCard({
      presenter,
      profile,
      agentRuntimeState,
    });
    return;
  }

  const primaryBinding = await resolveProfileShowPrimaryBinding(profile);
  presenter.line({
    code: "profile.show",
    text: `profile: ${profile.wakeLoopProfileId}`,
    data: {
      profile: profile.wakeLoopProfileId,
      wakeLoopProfileId: profile.wakeLoopProfileId,
      profileName: profile.profileName,
      profileHandle: profile.profileHandle,
      profileKind: profile.profileKind,
      ownerUserId: profile.ownerUserId,
      binding: buildAgentProfileBindingData(primaryBinding),
      ...buildAgentProfileStateData(profile),
      ...buildAgentProfileRuntimeData(agentRuntimeState),
    },
  });
}

async function resolveProfileShowPrimaryBinding(
  profile: WakeLoopProfile
): Promise<AgentProfilePrimaryAgentControllerReport | null> {
  if (profile.profileKind !== "agent") {
    return null;
  }
  return await resolveAgentProfilePrimaryAgentControllerReport({
    profile,
  });
}

function buildProfileShowJsonPayload(input: {
  agentRuntimeState: Awaited<
    ReturnType<typeof resolveAgentProfileRuntimeState>
  >;
  primaryBinding: AgentProfilePrimaryAgentControllerReport | null;
  profile: WakeLoopProfile;
}): Record<string, unknown> {
  return {
    profile: input.profile.wakeLoopProfileId,
    wakeLoopProfileId: input.profile.wakeLoopProfileId,
    profileName: input.profile.profileName,
    profileHandle: input.profile.profileHandle,
    profileKind: input.profile.profileKind,
    ownerUserId: input.profile.ownerUserId,
    status: input.profile.status,
    binding: buildAgentProfileBindingData(input.primaryBinding),
    runtime: buildAgentProfileRuntimeData(input.agentRuntimeState),
  };
}

function buildAgentProfileBindingData(
  primaryBinding: AgentProfilePrimaryAgentControllerReport | null
): Record<string, unknown> | null {
  if (!primaryBinding) {
    return null;
  }

  return {
    agentControllerConversationId:
      primaryBinding.primaryBinding.executionTarget
        ?.agentControllerConversationId ?? null,
    agentControllerPermissionMode:
      resolveNativeAgentControllerPermissionModeForController(
        primaryBinding.primaryBinding.agentControllerPermissionMode,
        { agentControllerRef: primaryBinding.agentControllerRef }
      ) ?? null,
    agentControllerRef: primaryBinding.agentControllerRef,
    wakeLoopServiceRuntimeId:
      primaryBinding.primaryBinding.executionTarget?.wakeLoopServiceRuntimeId ??
      null,
    bindingState: primaryBinding.bindingState,
    modelSelection: primaryBinding.primaryBinding.modelSelection ?? null,
    reasonCodes: primaryBinding.reasonCodes,
    routable: primaryBinding.routable,
  };
}

export async function runProfileCreate(input: {
  profileName?: string;
  kind?: string;
  agentController?: string;
  agentControllerConversationId?: string;
  controllerEnabled?: boolean;
  agentControllerPermissionModeInput?: string;
  confirmDangerousAgentControllerPermissionMode?: boolean;
  model?: string;
  reasoningEffort?: string;
  view?: string;
  entryChecksHandled?: boolean;
}) {
  const runtime = await resolveRuntimeContext({ view: input.view });
  if (!input.entryChecksHandled) {
    const authReady = await ensureProfileCommandAuthenticationOrCancel({
      action: "create",
      runtime,
    });
    if (!authReady) {
      return;
    }
  }
  const presenter = createPresenter(runtime);
  const allowPrompt = canUseInteractivePrompts(runtime);
  const draft = await resolveProfileCreateDraft({
    input,
    allowPrompt,
    resolvedView: runtime.resolvedView,
  });
  if (!draft) {
    return;
  }

  const created = await createProfileByKind({
    profileName: draft.profileName,
    profileKind: draft.profileKind,
    ...(draft.localAgentSelection
      ? { localAgentSelection: draft.localAgentSelection }
      : {}),
  });
  if (draft.setupResult?.status === "configured") {
    await persistAgentTransportSelection({
      agentControllerRef:
        draft.localAgentSelection?.agentControllerRef ??
        draft.setupResult.agentControllerRef,
      transportMode: draft.setupResult.transportMode,
    });
  }
  if (created.profileKind === "agent" && draft.localAgentSelection) {
    await ensureWakeLoopManagedProfileWorkspace({
      wakeLoopProfileId: created.wakeLoopProfileId,
    });
  }
  const agentRuntimeState: Awaited<
    ReturnType<typeof resolveAgentProfileRuntimeState>
  > = null;
  const selectionResult =
    runtime.resolvedView === "human"
      ? await setSelectedWakeLoopProfileId(created.wakeLoopProfileId)
      : null;

  await emitProfileCreateResult({
    runtime,
    presenter,
    profile: created,
    agentRuntimeState,
    selectionResult,
    setupResult: draft.setupResult,
  });
}

async function resolveProfileCreateDraft(input: {
  input: {
    profileName?: string;
    kind?: string;
    agentController?: string;
    agentControllerConversationId?: string;
    controllerEnabled?: boolean;
    agentControllerPermissionModeInput?: string;
    confirmDangerousAgentControllerPermissionMode?: boolean;
    model?: string;
    reasoningEffort?: string;
  };
  allowPrompt: boolean;
  resolvedView: "human" | "agent";
}): Promise<{
  profileKind: CreateProfileKind;
  profileName: string;
  localAgentSelection?: CreateProfileLocalAgentSelection;
  setupResult: AgentProfileSetupResult | null;
} | null> {
  const kindWasPrompted = !input.input.kind && input.allowPrompt;
  while (true) {
    const profileKind = await resolveCreateProfileKind({
      explicitKind: input.input.kind,
      allowPrompt: input.allowPrompt,
      resolvedView: input.resolvedView,
    });
    if (profileKind.status !== "submitted") {
      cancel("Cancelled.");
      return null;
    }

    while (true) {
      const profileName = await resolveCreateProfileName({
        explicitProfileName: input.input.profileName,
        allowPrompt: input.allowPrompt,
        resolvedView: input.resolvedView,
        profileKind: profileKind.value,
        allowBack: kindWasPrompted,
      });
      if (profileName.status === "cancelled") {
        cancel("Cancelled.");
        return null;
      }
      if (profileName.status === "back") {
        break;
      }

      const localAgentSelectionResolution =
        await resolveProfileCreateLocalAgentSelection({
          profileKind: profileKind.value,
          agentController: input.input.agentController,
          agentControllerConversationId:
            input.input.agentControllerConversationId,
          controllerEnabled: input.input.controllerEnabled,
          agentControllerPermissionModeInput:
            input.input.agentControllerPermissionModeInput,
          confirmDangerousAgentControllerPermissionMode:
            input.input.confirmDangerousAgentControllerPermissionMode,
          model: input.input.model,
          reasoningEffort: input.input.reasoningEffort,
          allowPrompt: input.allowPrompt,
          allowBackToCaller: !input.input.profileName,
        });
      if (localAgentSelectionResolution.status === "cancelled") {
        cancel("Cancelled.");
        return null;
      }
      if (localAgentSelectionResolution.status === "back") {
        continue;
      }

      return {
        profileKind: profileKind.value,
        profileName: profileName.value,
        localAgentSelection: localAgentSelectionResolution.localAgentSelection,
        setupResult: localAgentSelectionResolution.setupResult,
      };
    }
  }
}

async function resolveProfileCreateLocalAgentSelection(input: {
  profileKind: CreateProfileKind;
  agentController?: string;
  agentControllerConversationId?: string;
  controllerEnabled?: boolean;
  agentControllerPermissionModeInput?: string;
  confirmDangerousAgentControllerPermissionMode?: boolean;
  model?: string;
  reasoningEffort?: string;
  allowPrompt: boolean;
  allowBackToCaller: boolean;
}): Promise<
  | {
      status: "submitted";
      localAgentSelection?: CreateProfileLocalAgentSelection;
      setupResult: AgentProfileSetupResult | null;
    }
  | {
      status: "back";
    }
  | {
      status: "cancelled";
    }
> {
  const modelSelection = normalizeModelSelectionInput({
    model: input.model,
    reasoningEffort: input.reasoningEffort,
  });
  const controllerPermissionMode =
    await resolveAgentControllerPermissionModeInput({
      agentControllerRef: input.agentController,
      allowPrompt: input.allowPrompt,
      confirmDangerousAgentControllerPermissionMode:
        input.confirmDangerousAgentControllerPermissionMode,
      agentControllerPermissionModeInput:
        input.agentControllerPermissionModeInput,
      flagName: "--agent-controller-permission-mode",
    });
  const localAgentSelection = await normalizeCreateLocalAgentSelectionInput({
    profileKind: input.profileKind,
    agentController: input.agentController,
    agentControllerConversationId: input.agentControllerConversationId,
    controllerEnabled: input.controllerEnabled,
  });
  if (
    input.profileKind !== "agent" &&
    modelSelection.hasAnyModelSelectionInput
  ) {
    throw new Error("--model can only be used with --kind agent.");
  }
  if (
    input.profileKind !== "agent" &&
    controllerPermissionMode.hasAnyAgentControllerPermissionModeInput
  ) {
    throw new Error(
      "--agent-controller-permission-mode can only be used with --kind agent."
    );
  }
  if (localAgentSelection) {
    return {
      status: "submitted",
      localAgentSelection: applyCreateLocalAgentSelectionIntents(
        localAgentSelection,
        {
          controllerPermissionMode,
          modelSelection,
        }
      ),
      setupResult: null,
    };
  }
  if (modelSelection.hasAnyModelSelectionInput && !input.allowPrompt) {
    throw new Error(
      "--model requires --agent-controller when prompts are disabled."
    );
  }
  if (
    controllerPermissionMode.hasAnyAgentControllerPermissionModeInput &&
    !input.allowPrompt
  ) {
    throw new Error(
      "--agent-controller-permission-mode requires --agent-controller when prompts are disabled."
    );
  }
  if (
    input.profileKind !== "agent" ||
    localAgentSelection ||
    !input.allowPrompt
  ) {
    return {
      status: "submitted",
      ...(localAgentSelection ? { localAgentSelection } : {}),
      setupResult: null,
    };
  }

  return await resolvePromptedProfileCreateLocalAgentSelection({
    allowBackToCaller: input.allowBackToCaller,
    controllerPermissionMode,
    modelSelection,
  });
}

type ProfileCreateLocalAgentSelectionResolution =
  | {
      status: "submitted";
      localAgentSelection?: CreateProfileLocalAgentSelection;
      setupResult: AgentProfileSetupResult | null;
    }
  | {
      status: "back";
    }
  | {
      status: "cancelled";
    };

type NormalizedModelSelectionInput = ReturnType<
  typeof normalizeModelSelectionInput
>;

async function resolvePromptedProfileCreateLocalAgentSelection(input: {
  allowBackToCaller: boolean;
  controllerPermissionMode: NormalizedAgentControllerPermissionModeInput;
  modelSelection: NormalizedModelSelectionInput;
}): Promise<ProfileCreateLocalAgentSelectionResolution> {
  const setupResult = await resolveAgentProfileSetup({
    interactive: true,
    useAutoSelectionWhenNonInteractive: false,
    selectControllerMessage: "Choose an agent controller for this profile.",
    selectTransportModeMessage:
      "How should this profile connect to its agent controller?",
    selectResponsePolicyMessage: "When should this profile auto-reply?",
    skipControllerOptionLabel: "Skip for now",
    allowBackToCaller: input.allowBackToCaller,
  });
  if (setupResult.status === "cancelled") {
    return {
      status: "cancelled",
    };
  }
  if (setupResult.status === "back") {
    return {
      status: "back",
    };
  }
  if (
    setupResult.status !== "configured" &&
    (input.modelSelection.hasAnyModelSelectionInput ||
      input.controllerPermissionMode.hasAnyAgentControllerPermissionModeInput)
  ) {
    throw new Error(
      "--model and --agent-controller-permission-mode require an agent controller."
    );
  }
  return {
    status: "submitted",
    ...(setupResult.status === "configured"
      ? {
          localAgentSelection: applyCreateLocalAgentSelectionIntents(
            buildBuiltinLocalAgentSelection(setupResult.agentControllerRef),
            {
              controllerPermissionMode: input.controllerPermissionMode,
              modelSelection: input.modelSelection,
            }
          ),
        }
      : {}),
    setupResult,
  };
}

function applyCreateLocalAgentSelectionIntents(
  selection: CreateProfileLocalAgentSelection,
  input: {
    controllerPermissionMode: NormalizedAgentControllerPermissionModeInput;
    modelSelection: NormalizedModelSelectionInput;
  }
): CreateProfileLocalAgentSelection {
  if (input.controllerPermissionMode.agentControllerPermissionMode) {
    if (!selection.agentControllerRef) {
      throw new Error(
        "--agent-controller-permission-mode requires an agent controller."
      );
    }
    assertAgentControllerPermissionModeSupported({
      agentControllerPermissionMode:
        input.controllerPermissionMode.agentControllerPermissionMode,
      agentControllerRef: selection.agentControllerRef,
      flagName: "--agent-controller-permission-mode",
    });
  }
  return {
    ...selection,
    ...(input.controllerPermissionMode.agentControllerPermissionMode
      ? {
          agentControllerPermissionMode:
            input.controllerPermissionMode.agentControllerPermissionMode,
        }
      : {}),
    ...(input.modelSelection.modelId
      ? { modelId: input.modelSelection.modelId }
      : {}),
    ...(input.modelSelection.reasoningLevel
      ? { reasoningLevel: input.modelSelection.reasoningLevel }
      : {}),
  };
}

function buildBuiltinLocalAgentSelection(agentControllerRef: string): {
  agentControllerRef: string;
  controllerEnabled: true;
} {
  const parsed = parseAgentControllerRef(agentControllerRef);
  return {
    agentControllerRef: parsed?.ref ?? `builtin:${agentControllerRef}`,
    controllerEnabled: true,
  };
}

async function emitProfileCreateResult(input: {
  runtime: RuntimeContext;
  presenter: ReturnType<typeof createPresenter>;
  profile: WakeLoopProfile;
  agentRuntimeState: Awaited<
    ReturnType<typeof resolveAgentProfileRuntimeState>
  >;
  selectionResult: Awaited<
    ReturnType<typeof setSelectedWakeLoopProfileId>
  > | null;
  setupResult: AgentProfileSetupResult | null;
}): Promise<void> {
  if (input.runtime.resolvedView === "human") {
    if (!input.selectionResult) {
      throw new Error("profile.create selection result missing in human view");
    }
    renderProfileReadyCard({
      presenter: input.presenter,
      profile: input.profile,
      state: "created",
      agentRuntimeState: input.agentRuntimeState,
    });
    emitNonPersistentSelectionWarning({
      runtime: input.runtime,
      presenter: input.presenter,
      selectionPersistent: input.selectionResult.persistent,
      cliContextSource: input.selectionResult.cliContextSource,
    });
    await emitProfileCreateAgentSetupHint({
      presenter: input.presenter,
      resolvedView: input.runtime.resolvedView,
      profile: input.profile,
      setupResult: input.setupResult,
    });
    return;
  }

  input.presenter.line({
    code: "profile.created",
    text: `created Agent Profile identity: ${input.profile.wakeLoopProfileId}`,
    data: {
      wakeLoopProfileId: input.profile.wakeLoopProfileId,
      profileName: input.profile.profileName,
      profileHandle: input.profile.profileHandle,
      profileKind: input.profile.profileKind,
      ownerUserId: input.profile.ownerUserId,
      ...buildAgentProfileStateData(input.profile),
      ...buildAgentProfileRuntimeData(input.agentRuntimeState),
      currentProfileChanged: false,
      ...buildAgentCreateWakeReadinessData(input.profile),
      nextSteps: buildAgentCreateNextSteps(input.profile),
    },
  });
  await emitProfileCreateAgentSetupHint({
    presenter: input.presenter,
    resolvedView: input.runtime.resolvedView,
    profile: input.profile,
    setupResult: input.setupResult,
  });

  input.presenter.line({
    code: "profile.selection_unchanged",
    text: `created profile ${input.profile.wakeLoopProfileId}; current profile was not changed`,
    data: {
      createdProfileId: input.profile.wakeLoopProfileId,
      currentProfileChanged: false,
      reason:
        "Agent View profile creation does not change the acting identity. Pass --profile explicitly when this profile should act.",
      ...buildAgentCreateWakeReadinessData(input.profile),
      nextSteps: buildAgentCreateNextSteps(input.profile),
    },
  });
}

function emitProfileCreateAgentSetupHint(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  profile: WakeLoopProfile;
  setupResult: AgentProfileSetupResult | null;
}): void {
  if (!input.setupResult || input.setupResult.status === "configured") {
    return;
  }

  const prepareCommand = formatWakeLoopCliCommand(
    `wakeloop setup --profile ${input.profile.wakeLoopProfileId}`
  );
  const explicitLocalAgentSetupCommand = formatWakeLoopCliCommand(
    `wakeloop setup --profile ${input.profile.wakeLoopProfileId} --local-agent <local-agent-id> --view agent`
  );
  const summary =
    input.setupResult.status === "skipped_no_enabled_controllers"
      ? "No enabled agent controller is available, so the profile was created without one."
      : "Agent controller setup was skipped, so the profile was created without one.";

  if (input.resolvedView === "agent") {
    input.presenter.line({
      code: "profile.create.agent_setup.skipped",
      text: summary,
      data: {
        status: input.setupResult.status,
        ...(input.setupResult.status === "skipped_no_enabled_controllers" &&
        input.setupResult.reason
          ? { reason: input.setupResult.reason }
          : {}),
        profileId: input.profile.wakeLoopProfileId,
        nextSteps: [
          `For this existing profile, run ${explicitLocalAgentSetupCommand} when you know which local agent it should use.`,
          `Or run ${prepareCommand} in an interactive Terminal and choose the local agent there.`,
        ],
        localAgentSetupCommand: explicitLocalAgentSetupCommand,
      },
    });
    return;
  }

  input.presenter.line({
    code: "profile.create.agent_setup.skipped",
    text: formatInlineWakeLoopCliCommands(
      `${summary} For this existing profile, run \`wakeloop setup --profile ${input.profile.wakeLoopProfileId} --local-agent <local-agent-id>\` when you know which local agent it should use, or run \`${prepareCommand}\` in an interactive Terminal.`
    ),
  });
}

export async function runProfileSet(input: {
  profile?: string;
  view?: string;
  entryChecksHandled?: boolean;
}) {
  const runtime = await resolveRuntimeContext({ view: input.view });
  if (!input.entryChecksHandled) {
    const authReady = await ensureProfileCommandAuthenticationOrCancel({
      action: "set",
      runtime,
    });
    if (!authReady) {
      return;
    }
  }
  const presenter = createPresenter(runtime);

  const profileId = await resolveTargetProfile({
    explicitProfile: input.profile,
    action: "set",
    allowPrompt: canUseInteractivePrompts(runtime),
    resolvedView: runtime.resolvedView,
  });
  if (!profileId) {
    return;
  }

  const profile = await resolveWakeLoopProfile({
    wakeLoopProfileId: profileId,
  });
  const agentRuntimeState = await resolveAgentProfileRuntimeState(profile);
  assertProfileAllowedInView({
    profileId: profile.wakeLoopProfileId,
    profileKind: profile.profileKind,
    resolvedView: runtime.resolvedView,
    action: "set",
  });

  const selectionResult = await setSelectedWakeLoopProfileId(
    profile.wakeLoopProfileId
  );

  if (runtime.resolvedView === "human") {
    renderProfileReadyCard({
      presenter,
      profile,
      state: "selected",
      agentRuntimeState,
    });
    emitNonPersistentSelectionWarning({
      runtime,
      presenter,
      selectionPersistent: selectionResult.persistent,
      cliContextSource: selectionResult.cliContextSource,
    });
    return;
  }

  presenter.line({
    code: "profile.current_set",
    text: formatMessage(runtime.resolvedView, "profile.current_set", {
      profile: profile.wakeLoopProfileId,
    }),
    data: {
      profile: profile.wakeLoopProfileId,
      wakeLoopProfileId: profile.wakeLoopProfileId,
      profileName: profile.profileName,
      profileHandle: profile.profileHandle,
      profileKind: profile.profileKind,
      ownerUserId: profile.ownerUserId,
      ...buildAgentProfileStateData(profile),
      ...buildAgentProfileRuntimeData(agentRuntimeState),
    },
  });
  emitNonPersistentSelectionWarning({
    runtime,
    presenter,
    selectionPersistent: selectionResult.persistent,
    cliContextSource: selectionResult.cliContextSource,
  });
}

export async function runProfileDelete(input: {
  profile?: string;
  force?: boolean;
  view?: string;
  entryChecksHandled?: boolean;
}) {
  const runtime = await resolveRuntimeContext({ view: input.view });
  if (!input.entryChecksHandled) {
    const authReady = await ensureProfileCommandAuthenticationOrCancel({
      action: "delete",
      runtime,
    });
    if (!authReady) {
      return;
    }
  }
  const presenter = createPresenter(runtime);

  const visibleProfiles = await listVisibleProfilesForListByView(
    runtime.resolvedView
  );
  const current = await resolveCurrentProfileId({
    profiles: visibleProfiles,
    mutateSelection: false,
  });
  const profileIds = await resolveTargetProfilesForDelete({
    explicitProfile: input.profile,
    allowPrompt: canUseInteractivePrompts(runtime),
    resolvedView: runtime.resolvedView,
    cachedProfiles: visibleProfiles,
    presenter,
  });
  if (!profileIds) {
    return;
  }

  const profiles: WakeLoopProfile[] = [];
  for (const profileId of profileIds) {
    const profile = await resolveWakeLoopProfile({
      wakeLoopProfileId: profileId,
    });
    assertProfileAllowedInView({
      profileId: profile.wakeLoopProfileId,
      profileKind: profile.profileKind,
      resolvedView: runtime.resolvedView,
      action: "delete",
    });
    assertProfileDeletable(profile);
    profiles.push(profile);
  }

  const confirmed = await confirmDelete({
    profiles: profiles.map((profile) => profile.wakeLoopProfileId),
    force: input.force,
    allowPrompt: canUseInteractivePrompts(runtime),
    resolvedView: runtime.resolvedView,
  });
  if (!confirmed) {
    return;
  }

  const deletedProfileIds = new Set<string>();
  for (const profile of profiles) {
    await deleteWakeLoopProfile({
      wakeLoopProfileId: profile.wakeLoopProfileId,
    });
    deletedProfileIds.add(profile.wakeLoopProfileId);
    presenter.line({
      code: "profile.deleted",
      text: formatMessage(runtime.resolvedView, "profile.deleted", {
        profile: profile.wakeLoopProfileId,
      }),
      data: { profile: profile.wakeLoopProfileId },
    });
  }

  const remainingProfiles = (
    await listVisibleProfilesForListByView(runtime.resolvedView)
  ).filter((item) => !deletedProfileIds.has(item.wakeLoopProfileId));
  const remaining = remainingProfiles.map((item) => item.wakeLoopProfileId);
  const selected = await getSelectedWakeLoopProfileId();
  const selectedMissing = Boolean(selected && !remaining.includes(selected));
  const shouldSwitch =
    selectedMissing || (current ? deletedProfileIds.has(current) : false);
  if (!shouldSwitch) {
    return;
  }
  const fallback = await resolveCurrentProfileId({
    profiles: remainingProfiles,
    mutateSelection: false,
  });

  if (fallback) {
    await setSelectedWakeLoopProfileId(fallback);
    presenter.line({
      code: "profile.current_switched",
      text: formatMessage(runtime.resolvedView, "profile.current_switched", {
        profile: fallback,
      }),
      data: { profile: fallback },
    });
    return;
  }

  await clearSelectedWakeLoopProfileId();
  presenter.line({
    code: "profile.no_active",
    text: formatMessage(runtime.resolvedView, "profile.no_active"),
  });
}

export async function runProfileUpdate(input: {
  profile?: string;
  profileName?: string;
  agentController?: string;
  agentControllerConversationId?: string;
  agentControllerPermissionModeInput?: string;
  confirmDangerousAgentControllerPermissionMode?: boolean;
  model?: string;
  reasoningEffort?: string;
  workspaceMode?: string;
  workspacePath?: string;
  view?: string;
  entryChecksHandled?: boolean;
  allowBackToCaller?: boolean;
}): Promise<"back" | undefined> {
  const runtime = await resolveRuntimeContext({ view: input.view });
  if (!input.entryChecksHandled) {
    const authReady = await ensureProfileCommandAuthenticationOrCancel({
      action: "update",
      runtime,
    });
    if (!authReady) {
      return;
    }
  }
  const presenter = createPresenter(runtime);
  const allowPrompt = canUseInteractivePrompts(runtime);
  const explicitProfileId = normalizeOptionalString(input.profile);

  const visibleProfiles = explicitProfileId
    ? []
    : await listVisibleProfilesForListByView(runtime.resolvedView);
  const current = explicitProfileId
    ? null
    : await resolveCurrentProfileId({
        profiles: visibleProfiles,
        mutateSelection: false,
      });

  const resolvedSelection = await resolveProfileUpdateSelection({
    explicitProfileId,
    allowPrompt,
    resolvedView: runtime.resolvedView,
    presenter,
    visibleProfiles,
    currentProfileId: current,
    profileName: input.profileName,
    agentController: input.agentController,
    agentControllerConversationId: input.agentControllerConversationId,
    agentControllerPermissionModeInput:
      input.agentControllerPermissionModeInput,
    confirmDangerousAgentControllerPermissionMode:
      input.confirmDangerousAgentControllerPermissionMode,
    model: input.model,
    reasoningEffort: input.reasoningEffort,
    workspaceMode: input.workspaceMode,
    workspacePath: input.workspacePath,
  });
  if (resolvedSelection.status !== "submitted") {
    return handleProfileUpdateExit({
      status: resolvedSelection.status,
      allowBackToCaller: input.allowBackToCaller === true,
    });
  }

  const { profile, payload } = resolvedSelection.value;
  const updated = await applyProfileUpdatePatch({
    profile,
    payload,
  });
  await postLocalParticipationMutationReconcile({
    codePrefix: "profile.update",
    ownerUserId: updated.ownerUserId,
    presenter,
    shouldReconcile: false,
  });
  const agentRuntimeState = await resolveAgentProfileRuntimeState(updated);
  if (runtime.resolvedView === "human") {
    renderProfileReadyCard({
      presenter,
      profile: updated,
      state: "updated",
      agentRuntimeState,
    });
    return;
  }

  presenter.line({
    code: "profile.updated",
    text: `updated profile: ${updated.wakeLoopProfileId}`,
    data: {
      profile: updated.wakeLoopProfileId,
      wakeLoopProfileId: updated.wakeLoopProfileId,
      profileName: updated.profileName,
      profileHandle: updated.profileHandle,
      profileKind: updated.profileKind,
      ownerUserId: updated.ownerUserId,
      updatedFields: payload.updatedFields,
      binding: buildAgentProfileBindingData(
        await resolveProfileShowPrimaryBinding(updated)
      ),
      ...buildAgentProfileStateData(updated),
      ...buildAgentProfileRuntimeData(agentRuntimeState),
      nextSteps: buildAgentUpdateNextSteps(updated),
    },
  });
}

async function resolveProfileUpdateSelection(input: {
  explicitProfileId: string | null;
  allowPrompt: boolean;
  resolvedView: "human" | "agent";
  presenter: ReturnType<typeof createPresenter>;
  visibleProfiles: WakeLoopProfile[];
  currentProfileId: string | null;
  profileName?: string;
  agentController?: string;
  agentControllerConversationId?: string;
  agentControllerPermissionModeInput?: string;
  confirmDangerousAgentControllerPermissionMode?: boolean;
  model?: string;
  reasoningEffort?: string;
  workspaceMode?: string;
  workspacePath?: string;
}): Promise<
  PromptFlowResult<{
    profile: WakeLoopProfile;
    payload: ProfileUpdatePayload;
  }>
> {
  while (true) {
    const profileId = await resolveProfileIdForUpdate({
      explicitProfile: input.explicitProfileId ?? undefined,
      allowPrompt: input.allowPrompt,
      resolvedView: input.resolvedView,
      presenter: input.presenter,
      visibleProfiles: input.visibleProfiles,
      currentProfileId: input.currentProfileId,
    });
    if (profileId.status !== "submitted") {
      return profileId;
    }

    const selection = await resolveProfileUpdateSelectionForProfile({
      profileId: profileId.value,
      resolvedView: input.resolvedView,
      allowPrompt: input.allowPrompt,
      profileName: input.profileName,
      agentController: input.agentController,
      agentControllerConversationId: input.agentControllerConversationId,
      agentControllerPermissionModeInput:
        input.agentControllerPermissionModeInput,
      confirmDangerousAgentControllerPermissionMode:
        input.confirmDangerousAgentControllerPermissionMode,
      model: input.model,
      reasoningEffort: input.reasoningEffort,
      workspaceMode: input.workspaceMode,
      workspacePath: input.workspacePath,
    });
    if (selection.status === "back" && input.explicitProfileId === null) {
      continue;
    }
    return selection;
  }
}

async function resolveProfileUpdateSelectionForProfile(input: {
  profileId: string;
  resolvedView: "human" | "agent";
  allowPrompt: boolean;
  profileName?: string;
  agentController?: string;
  agentControllerConversationId?: string;
  agentControllerPermissionModeInput?: string;
  confirmDangerousAgentControllerPermissionMode?: boolean;
  model?: string;
  reasoningEffort?: string;
  workspaceMode?: string;
  workspacePath?: string;
}): Promise<
  PromptFlowResult<{
    profile: WakeLoopProfile;
    payload: ProfileUpdatePayload;
  }>
> {
  const profile = await resolveWakeLoopProfile({
    wakeLoopProfileId: input.profileId,
  });
  assertProfileAllowedInView({
    profileId: profile.wakeLoopProfileId,
    profileKind: profile.profileKind,
    resolvedView: input.resolvedView,
    action: "update",
  });

  const payload = await resolveProfileUpdatePayload({
    profile,
    allowPrompt: input.allowPrompt,
    resolvedView: input.resolvedView,
    profileName: input.profileName,
    agentController: input.agentController,
    agentControllerConversationId: input.agentControllerConversationId,
    agentControllerPermissionModeInput:
      input.agentControllerPermissionModeInput,
    confirmDangerousAgentControllerPermissionMode:
      input.confirmDangerousAgentControllerPermissionMode,
    model: input.model,
    reasoningEffort: input.reasoningEffort,
    workspaceMode: input.workspaceMode,
    workspacePath: input.workspacePath,
  });
  if (payload.status !== "submitted") {
    return payload;
  }

  return {
    status: "submitted",
    value: {
      profile,
      payload: payload.value,
    },
  };
}

function handleProfileUpdateExit(input: {
  status: "back" | "cancelled";
  allowBackToCaller: boolean;
}): "back" | undefined {
  if (input.status === "back" && input.allowBackToCaller) {
    return "back";
  }
  cancel("Cancelled.");
  return undefined;
}

async function applyProfileUpdatePatch(input: {
  profile: WakeLoopProfile;
  payload: ProfileUpdatePayload;
}): Promise<WakeLoopProfile> {
  const hasProfilePatch = Boolean(input.payload.profileName);
  const updated = hasProfilePatch
    ? await updateWakeLoopProfile({
        wakeLoopProfileId: input.profile.wakeLoopProfileId,
        ...(input.payload.profileName
          ? { profileName: input.payload.profileName }
          : {}),
      })
    : input.profile;
  await applyAgentProfileWorkspace({
    profile: updated,
    workspaceMode: input.payload.workspaceMode,
    customWorkspacePath: input.payload.customWorkspacePath,
  });
  await applyAgentProfileControllerBinding({
    agentControllerConversationId: input.payload.agentControllerConversationId,
    agentControllerRef: input.payload.agentControllerRef,
    modelId: input.payload.modelId,
    profile: updated,
    reasoningLevel: input.payload.reasoningLevel,
  });
  await applyAgentProfileControllerPermissionMode({
    agentControllerPermissionMode: input.payload.agentControllerPermissionMode,
    profile: updated,
  });
  return updated;
}

async function resolveProfileIdForUpdate(input: {
  explicitProfile: string | undefined;
  allowPrompt: boolean;
  resolvedView: "human" | "agent";
  presenter: ReturnType<typeof createPresenter>;
  visibleProfiles: WakeLoopProfile[];
  currentProfileId: string | null;
}): Promise<PromptFlowResult<string>> {
  const fromInput = String(input.explicitProfile ?? "").trim();
  if (fromInput) {
    return {
      status: "submitted",
      value: fromInput,
    };
  }
  const profileUpdateExamples = await resolveProfileUpdateCommandExamples();

  if (input.resolvedView === "agent") {
    if (input.visibleProfiles.length === 0) {
      throw createProfileActionGuideError(
        "update",
        "no agent profiles found; create one after signing in.",
        toProfileActionGuideExamples(profileUpdateExamples)
      );
    }
    throw createProfileActionGuideError(
      "update",
      "explicit profile argument is required in agent view.",
      toProfileActionGuideExamples(profileUpdateExamples)
    );
  }

  if (!input.allowPrompt) {
    emitProfileUpdateCandidatesForHuman({
      presenter: input.presenter,
      profiles: input.visibleProfiles,
      currentProfileId: input.currentProfileId,
      examples: profileUpdateExamples,
    });
    if (input.visibleProfiles.length === 0) {
      throw new Error(formatMessage(input.resolvedView, "profile.no_profiles"));
    }
    throw new Error(
      formatMessage(input.resolvedView, "profile.target.missing", {
        action: "update",
      })
    );
  }

  const selected = await promptSearchSelect({
    message: "Choose a profile to update",
    items: input.visibleProfiles.map((profile) =>
      buildProfileSearchItem({
        profile,
        current: input.currentProfileId,
        resolvedView: input.resolvedView,
      })
    ),
    maxVisible: 8,
    allowBack: true,
    backLabel: "Back",
  });
  if (selected.status === "cancelled") {
    return {
      status: "cancelled",
    };
  }
  if (selected.status === "back") {
    return {
      status: "back",
    };
  }
  return {
    status: "submitted",
    value: selected.value,
  };
}

function emitProfileUpdateCandidatesForHuman(input: {
  presenter: ReturnType<typeof createPresenter>;
  profiles: WakeLoopProfile[];
  currentProfileId: string | null;
  examples: ProfileUpdateCommandExamples;
}): void {
  input.presenter.line({
    code: "profile.update.candidates.header",
    text: "Available profiles to update:",
  });
  if (input.profiles.length === 0) {
    input.presenter.line({
      code: "profile.update.candidates.empty",
      text: "- (none)",
    });
  }
  for (const profile of input.profiles) {
    const selected =
      profile.wakeLoopProfileId === input.currentProfileId ? " (selected)" : "";
    input.presenter.line({
      code: "profile.update.candidates.item",
      text:
        `- ${normalizeProfileDisplayName(profile)} (${profile.wakeLoopProfileId})${selected}` +
        ` · ${profile.profileKind} · updatable: ${resolveUpdatableFields(profile).join(", ")}`,
    });
  }

  const firstHuman = input.profiles.find(
    (profile) => profile.profileKind === "human"
  );
  const firstAgent = input.profiles.find(
    (profile) => profile.profileKind === "agent"
  );
  input.presenter.line({
    code: "profile.update.candidates.next",
    text: "Now: run one of these examples to continue update without interactive prompts:",
  });
  if (firstHuman) {
    input.presenter.line({
      code: "profile.update.candidates.next.item",
      text: `- ${formatWakeLoopCliCommand(
        `wakeloop profiles update ${firstHuman.wakeLoopProfileId} --name "<new-name>"`
      )}`,
    });
  }
  if (firstAgent) {
    input.presenter.line({
      code: "profile.update.candidates.next.item",
      text: `- ${formatWakeLoopCliCommand(
        `wakeloop setup --profile ${firstAgent.wakeLoopProfileId}`
      )}`,
    });
    input.presenter.line({
      code: "profile.update.candidates.next.item",
      text: `- ${formatWakeLoopCliCommand(
        `wakeloop profiles update ${firstAgent.wakeLoopProfileId} --workspace-mode wakeloop-managed`
      )}`,
    });
    input.presenter.line({
      code: "profile.update.candidates.next.item",
      text: `- ${formatWakeLoopCliCommand(
        `wakeloop profiles update ${firstAgent.wakeLoopProfileId} --workspace-mode custom --workspace-path /absolute/path`
      )}`,
    });
  }
}

async function resolveProfileUpdatePayload(input: {
  profile: WakeLoopProfile;
  allowPrompt: boolean;
  resolvedView: "human" | "agent";
  profileName?: string;
  agentController?: string;
  agentControllerConversationId?: string;
  agentControllerPermissionModeInput?: string;
  confirmDangerousAgentControllerPermissionMode?: boolean;
  model?: string;
  reasoningEffort?: string;
  workspaceMode?: string;
  workspacePath?: string;
}): Promise<PromptFlowResult<ProfileUpdatePayload>> {
  const profileName = normalizeOptionalString(input.profileName);
  const explicitAgentController =
    await resolveExplicitAgentControllerPatchForProfile({
      agentController: input.agentController,
      agentControllerConversationId: input.agentControllerConversationId,
      profile: input.profile,
    });
  const explicitWorkspace = resolveExplicitWorkspacePatchForProfile({
    profile: input.profile,
    workspaceMode: input.workspaceMode,
    workspacePath: input.workspacePath,
  });
  const explicitModelSelection = resolveExplicitModelSelectionPatchForProfile({
    model: input.model,
    profile: input.profile,
    reasoningEffort: input.reasoningEffort,
  });
  const explicitAgentControllerPermissionMode =
    await resolveExplicitAgentControllerPermissionModePatchForProfile({
      agentControllerRef: explicitAgentController?.agentControllerRef,
      allowPrompt: input.allowPrompt,
      confirmDangerousAgentControllerPermissionMode:
        input.confirmDangerousAgentControllerPermissionMode,
      agentControllerPermissionModeInput:
        input.agentControllerPermissionModeInput,
      profile: input.profile,
    });

  if (
    profileName ||
    explicitAgentController ||
    explicitWorkspace ||
    explicitModelSelection ||
    explicitAgentControllerPermissionMode
  ) {
    return {
      status: "submitted",
      value: {
        ...(profileName ? { profileName } : {}),
        ...(explicitAgentController ?? {}),
        ...(explicitWorkspace ?? {}),
        ...(explicitModelSelection ?? {}),
        ...(explicitAgentControllerPermissionMode ?? {}),
        updatedFields: [
          ...(profileName ? ["profileName"] : []),
          ...(explicitAgentController?.updatedFields ?? []),
          ...(explicitWorkspace?.updatedFields ?? []),
          ...(explicitModelSelection?.updatedFields ?? []),
          ...(explicitAgentControllerPermissionMode?.updatedFields ?? []),
        ],
      },
    };
  }

  if (!input.allowPrompt) {
    throw new Error(
      formatMessage(input.resolvedView, "profile.update.missing_changes")
    );
  }

  return await promptProfileUpdatePayload(input.profile);
}

function resolveExplicitWorkspacePatchForProfile(input: {
  profile: WakeLoopProfile;
  workspaceMode?: string;
  workspacePath?: string;
}): {
  workspaceMode: "wakeloop_managed" | "custom";
  customWorkspacePath?: string;
  updatedFields: string[];
} | null {
  const normalizedMode = normalizeWorkspaceModeInput(input.workspaceMode);
  const normalizedWorkspacePath = normalizeOptionalString(input.workspacePath);
  if (!(normalizedMode || normalizedWorkspacePath)) {
    return null;
  }

  if (input.profile.profileKind !== "agent") {
    throw new Error("Workspace can only be configured for agent profiles.");
  }
  if (!normalizedMode) {
    throw new Error(
      "--workspace-path can only be used with --workspace-mode custom."
    );
  }
  if (normalizedMode === "wakeloop_managed") {
    if (normalizedWorkspacePath) {
      throw new Error(
        "--workspace-path can only be used with --workspace-mode custom."
      );
    }
    return {
      updatedFields: ["workspace"],
      workspaceMode: "wakeloop_managed",
    };
  }
  if (!normalizedWorkspacePath) {
    throw new Error(
      "--workspace-path is required when --workspace-mode custom is used."
    );
  }
  return {
    customWorkspacePath: normalizedWorkspacePath,
    updatedFields: ["workspace"],
    workspaceMode: "custom",
  };
}

function resolveExplicitModelSelectionPatchForProfile(input: {
  model?: string;
  profile: WakeLoopProfile;
  reasoningEffort?: string;
}): {
  modelId: string;
  reasoningLevel?: string;
  updatedFields: string[];
} | null {
  const modelSelection = normalizeModelSelectionInput({
    model: input.model,
    reasoningEffort: input.reasoningEffort,
  });
  if (!modelSelection.hasAnyModelSelectionInput) {
    return null;
  }
  if (input.profile.profileKind !== "agent") {
    throw new Error(
      "Model selection can only be configured for agent profiles."
    );
  }
  if (!modelSelection.modelId) {
    throw new Error("--model is required.");
  }
  return {
    modelId: modelSelection.modelId,
    ...(modelSelection.reasoningLevel
      ? { reasoningLevel: modelSelection.reasoningLevel }
      : {}),
    updatedFields: ["modelSelection"],
  };
}

async function resolveExplicitAgentControllerPermissionModePatchForProfile(input: {
  agentControllerRef?: string;
  allowPrompt: boolean;
  confirmDangerousAgentControllerPermissionMode?: boolean;
  agentControllerPermissionModeInput?: string;
  profile: WakeLoopProfile;
}): Promise<{
  agentControllerPermissionMode: AgentControllerPermissionMode;
  updatedFields: string[];
} | null> {
  if (!normalizeOptionalString(input.agentControllerPermissionModeInput)) {
    return null;
  }
  if (input.profile.profileKind !== "agent") {
    throw new Error(
      "Agent Controller Permission Mode can only be configured for agent profiles."
    );
  }
  const currentBinding = input.agentControllerRef
    ? null
    : await resolveAgentProfilePrimaryAgentControllerReport({
        profile: input.profile,
      });
  const agentControllerRef =
    input.agentControllerRef ?? currentBinding?.agentControllerRef ?? null;
  if (!agentControllerRef) {
    throw new Error(
      "--agent-controller-permission-mode requires an existing Agent Profile Binding or --agent-controller."
    );
  }
  const controllerPermissionMode =
    await resolveAgentControllerPermissionModeInput({
      agentControllerRef,
      allowPrompt: input.allowPrompt,
      confirmDangerousAgentControllerPermissionMode:
        input.confirmDangerousAgentControllerPermissionMode,
      agentControllerPermissionModeInput:
        input.agentControllerPermissionModeInput,
      flagName: "--agent-controller-permission-mode",
    });
  if (!controllerPermissionMode.agentControllerPermissionMode) {
    throw new Error("--agent-controller-permission-mode is required.");
  }
  return {
    agentControllerPermissionMode:
      controllerPermissionMode.agentControllerPermissionMode,
    updatedFields: ["agentControllerPermissionMode"],
  };
}

async function resolveExplicitAgentControllerPatchForProfile(input: {
  agentController?: string;
  agentControllerConversationId?: string;
  profile: WakeLoopProfile;
}): Promise<{
  agentControllerRef: string;
  agentControllerConversationId?: string;
  updatedFields: string[];
} | null> {
  const agentController = normalizeOptionalString(input.agentController);
  const agentControllerConversationId = normalizeOptionalString(
    input.agentControllerConversationId
  );
  if (!(agentController || agentControllerConversationId)) {
    return null;
  }
  if (input.profile.profileKind !== "agent") {
    throw new Error(
      "Agent controller can only be configured for agent profiles."
    );
  }
  if (!agentController) {
    throw new Error(
      "--agent-controller-conversation-id requires --agent-controller."
    );
  }
  const parsed = parseAgentControllerRef(agentController);
  if (!parsed) {
    throw new Error("--agent-controller must use builtin:<id> or custom:<id>");
  }
  if (parsed.kind === "builtin") {
    await assertBuiltinAgentControllerRefRunnable(parsed.ref);
  }
  return {
    agentControllerRef: parsed.ref,
    ...(agentControllerConversationId ? { agentControllerConversationId } : {}),
    updatedFields: [
      "agentController",
      ...(agentControllerConversationId
        ? ["agentControllerConversationId"]
        : []),
    ],
  };
}

async function applyAgentProfileControllerBinding(input: {
  agentControllerConversationId?: string;
  agentControllerRef?: string;
  modelId?: string;
  profile: WakeLoopProfile;
  reasoningLevel?: string;
}): Promise<void> {
  const agentControllerRef = normalizeOptionalString(input.agentControllerRef);
  const hasModelSelectionPatch = input.modelId !== undefined;
  if (!(agentControllerRef || hasModelSelectionPatch)) {
    return;
  }
  if (input.profile.profileKind !== "agent") {
    throw new Error(
      "Agent controller can only be configured for agent profiles."
    );
  }
  if (!agentControllerRef && hasModelSelectionPatch) {
    const binding = await updateAgentProfilePrimaryBindingModelSelection({
      modelId: input.modelId ?? "",
      profile: input.profile,
      reasoningLevel: input.reasoningLevel,
    });
    if (binding.status !== "accepted") {
      throw new Error(
        `Agent Profile Binding update failed: ${binding.reasonCodes.join(", ")}`
      );
    }
    return;
  }
  const currentBinding = agentControllerRef
    ? null
    : await resolveAgentProfilePrimaryAgentControllerReport({
        profile: input.profile,
      });
  const resolvedAgentControllerRef =
    agentControllerRef ?? currentBinding?.agentControllerRef ?? null;
  if (!resolvedAgentControllerRef) {
    throw new Error(
      "--model requires an existing Agent Profile Binding or --agent-controller."
    );
  }
  const agentControllerConversationId =
    agentControllerRef === null
      ? (currentBinding?.primaryBinding.executionTarget
          ?.agentControllerConversationId ?? undefined)
      : input.agentControllerConversationId;
  const runtimeReportSubmission = await submitRuntimeAgentControllerReport({
    agentControllerRef: resolvedAgentControllerRef,
    diagnosticProfileId: input.profile.wakeLoopProfileId,
    localParticipationState: "ready",
    reasonCodes: ["runtime.agent_controller_report.ready"],
  });
  const binding = await ensureAgentProfileRuntimeBinding({
    agentControllerConversationId,
    modelId: input.modelId,
    profile: input.profile,
    reasoningLevel: input.reasoningLevel,
    runtimeReportSubmission,
    writeMode: "reconnect",
  });
  if (binding.status !== "accepted") {
    throw new Error(
      `Agent Profile Binding update failed: ${binding.reasonCodes.join(", ")}`
    );
  }
}

async function applyAgentProfileControllerPermissionMode(input: {
  agentControllerPermissionMode?: AgentControllerPermissionMode;
  profile: WakeLoopProfile;
}): Promise<void> {
  if (!input.agentControllerPermissionMode) {
    return;
  }
  if (input.profile.profileKind !== "agent") {
    throw new Error(
      "Agent Controller Permission Mode can only be configured for agent profiles."
    );
  }
  const binding =
    await updateAgentProfilePrimaryBindingAgentControllerPermissionMode({
      agentControllerPermissionMode: input.agentControllerPermissionMode,
      profile: input.profile,
    });
  if (binding.status !== "accepted") {
    throw new Error(
      `Agent Profile Binding update failed: ${binding.reasonCodes.join(", ")}`
    );
  }
}

type ProfileUpdatePayload = {
  profileName?: string;
  agentControllerRef?: string;
  agentControllerConversationId?: string;
  agentControllerPermissionMode?: AgentControllerPermissionMode;
  modelId?: string;
  reasoningLevel?: string;
  workspaceMode?: "wakeloop_managed" | "custom";
  customWorkspacePath?: string;
  updatedFields: string[];
};

async function promptProfileUpdatePayload(
  profile: WakeLoopProfile
): Promise<PromptFlowResult<ProfileUpdatePayload>> {
  while (true) {
    const selectedField = await selectProfileUpdateField(profile);
    if (selectedField.status !== "submitted") {
      return selectedField;
    }

    const payloadByField: Record<
      UpdateFieldSelection,
      () => Promise<PromptFlowResult<ProfileUpdatePayload>>
    > = {
      agent_controller: () => promptAgentControllerPayload(profile),
      agent_controller_permission_mode: () =>
        promptAgentControllerPermissionModePayload(profile),
      name: () => promptProfileNamePayload(profile.profileName),
      workspace: () => promptWorkspacePayload(profile),
    };

    const payload = await payloadByField[selectedField.value]();
    if (payload.status === "back") {
      continue;
    }
    return payload;
  }
}

async function selectProfileUpdateField(
  profile: WakeLoopProfile
): Promise<PromptFlowResult<UpdateFieldSelection>> {
  while (true) {
    const selectedField = await select({
      message: "What do you want to update?",
      options: [
        {
          value: "name",
          label: "Profile name",
        },
        ...(profile.profileKind === "agent"
          ? ([
              {
                value: "agent_controller",
                label: "Agent controller",
              },
              {
                value: "agent_controller_permission_mode",
                label: "Agent Controller Permission Mode",
              },
              {
                value: "workspace",
                label: "Workspace",
              },
            ] as const)
          : []),
        {
          value: BACK_UPDATE_FIELD_SELECTION,
          label: "Back",
        },
      ],
    });

    if (isCancel(selectedField)) {
      return {
        status: "cancelled",
      };
    }
    if (selectedField === BACK_UPDATE_FIELD_SELECTION) {
      return {
        status: "back",
      };
    }

    return {
      status: "submitted",
      value: selectedField as UpdateFieldSelection,
    };
  }
}

async function promptWorkspacePayload(
  profile: WakeLoopProfile
): Promise<PromptFlowResult<ProfileUpdatePayload>> {
  if (profile.profileKind !== "agent") {
    throw new Error("Workspace can only be configured for agent profiles.");
  }
  while (true) {
    const selectedWorkspaceMode = await select({
      message: "Workspace",
      options: [
        {
          value: "wakeloop_managed",
          label: "WakeLoop-managed workspace",
          hint: "Use the workspace WakeLoop manages for this profile.",
        },
        {
          value: "custom",
          label: "Custom folder",
          hint: "Use a different folder on this device.",
        },
        {
          value: BACK_UPDATE_FIELD_SELECTION,
          label: "Back",
        },
      ],
    });
    if (isCancel(selectedWorkspaceMode)) {
      return {
        status: "cancelled",
      };
    }
    if (selectedWorkspaceMode === BACK_UPDATE_FIELD_SELECTION) {
      return {
        status: "back",
      };
    }
    if (selectedWorkspaceMode === "wakeloop_managed") {
      return {
        status: "submitted",
        value: {
          updatedFields: ["workspace"],
          workspaceMode: "wakeloop_managed",
        },
      };
    }

    const workspacePath = await promptTextEntry({
      message: "Custom workspace path",
      placeholder: "/absolute/path",
      allowBack: true,
      backLabel: "Back",
      requiredMessage: "Custom workspace path is required.",
    });
    if (workspacePath.status === "cancelled") {
      return {
        status: "cancelled",
      };
    }
    if (workspacePath.status === "back") {
      continue;
    }
    const customWorkspacePath = normalizeOptionalString(workspacePath.value);
    if (!customWorkspacePath) {
      throw new Error("Custom workspace path is required.");
    }
    return {
      status: "submitted",
      value: {
        customWorkspacePath,
        updatedFields: ["workspace"],
        workspaceMode: "custom",
      },
    };
  }
}

async function promptAgentControllerPayload(
  profile: WakeLoopProfile
): Promise<PromptFlowResult<ProfileUpdatePayload>> {
  if (profile.profileKind !== "agent") {
    throw new Error(
      "Agent controller can only be configured for agent profiles."
    );
  }
  const agentController = await promptTextEntry({
    message: "Agent controller",
    placeholder: "builtin:codex",
    allowBack: true,
    backLabel: "Back",
    requiredMessage: "Agent controller is required.",
    validate: (value) =>
      parseAgentControllerRef(value)
        ? null
        : "Use builtin:<id> or custom:<id>.",
  });
  if (agentController.status === "cancelled") {
    return {
      status: "cancelled",
    };
  }
  if (agentController.status === "back") {
    return {
      status: "back",
    };
  }

  const agentControllerConversationId = await promptTextEntry({
    message: "Agent controller conversation ID",
    description:
      "Optional private Agent Controller conversation ID for this Agent Profile.",
    placeholder: "optional",
    allowBack: true,
    allowEmpty: true,
    backLabel: "Back",
  });
  if (agentControllerConversationId.status === "cancelled") {
    return {
      status: "cancelled",
    };
  }
  if (agentControllerConversationId.status === "back") {
    return {
      status: "back",
    };
  }

  const patch = await resolveExplicitAgentControllerPatchForProfile({
    agentController: agentController.value,
    agentControllerConversationId: agentControllerConversationId.value,
    profile,
  });
  if (!patch) {
    throw new Error("Agent controller is required.");
  }
  return {
    status: "submitted",
    value: patch,
  };
}

async function promptAgentControllerPermissionModePayload(
  profile: WakeLoopProfile
): Promise<PromptFlowResult<ProfileUpdatePayload>> {
  if (profile.profileKind !== "agent") {
    throw new Error(
      "Agent Controller Permission Mode can only be configured for agent profiles."
    );
  }
  const primaryBinding = await resolveAgentProfilePrimaryAgentControllerReport({
    profile,
  });
  if (!primaryBinding) {
    throw new Error(
      "Connect an agent controller before editing Agent Controller Permission Mode."
    );
  }
  const options = listAgentControllerPermissionModeOptions({
    agentControllerRef: primaryBinding.agentControllerRef,
  });
  if (options.length === 0) {
    throw new Error(
      `Agent Controller Permission Mode is not supported by ${primaryBinding.agentControllerRef}.`
    );
  }
  const currentPolicy = resolveAgentControllerPermissionModeForController(
    primaryBinding.primaryBinding.agentControllerPermissionMode,
    { agentControllerRef: primaryBinding.agentControllerRef }
  );
  const fieldLabel = getAgentControllerPermissionModeFieldLabel({
    agentControllerRef: primaryBinding.agentControllerRef,
  });
  const selectedPolicy = await select({
    message: fieldLabel,
    options: [
      ...options.map((option) => ({
        value: option.value,
        label: option.label,
        hint:
          option.value === currentPolicy
            ? `${option.description} Current.`
            : option.description,
      })),
      {
        value: BACK_UPDATE_FIELD_SELECTION,
        label: "Back",
      },
    ],
  });
  if (isCancel(selectedPolicy)) {
    return {
      status: "cancelled",
    };
  }
  if (selectedPolicy === BACK_UPDATE_FIELD_SELECTION) {
    return {
      status: "back",
    };
  }

  const controllerPermissionMode =
    await resolveAgentControllerPermissionModeInput({
      agentControllerRef: primaryBinding.agentControllerRef,
      allowPrompt: true,
      agentControllerPermissionModeInput: String(selectedPolicy),
      flagName: fieldLabel,
    });
  if (!controllerPermissionMode.agentControllerPermissionMode) {
    throw new Error(`${fieldLabel} is required.`);
  }
  return {
    status: "submitted",
    value: {
      agentControllerPermissionMode:
        controllerPermissionMode.agentControllerPermissionMode,
      updatedFields: ["agentControllerPermissionMode"],
    },
  };
}

async function promptProfileNamePayload(
  currentProfileName: string
): Promise<PromptFlowResult<ProfileUpdatePayload>> {
  const nextName = await promptTextEntry({
    message: "New profile name",
    placeholder: currentProfileName,
    allowBack: true,
    backLabel: "Back",
    requiredMessage: "Profile name is required.",
  });
  if (nextName.status === "cancelled") {
    return {
      status: "cancelled",
    };
  }
  if (nextName.status === "back") {
    return {
      status: "back",
    };
  }
  const profileName = normalizeOptionalString(nextName.value);
  if (!profileName) {
    throw new Error("profileName is required");
  }
  return {
    status: "submitted",
    value: {
      profileName,
      updatedFields: ["profileName"],
    },
  };
}

function emitNonPersistentSelectionWarning(input: {
  runtime: Awaited<ReturnType<typeof resolveRuntimeContext>>;
  presenter: ReturnType<typeof createPresenter>;
  selectionPersistent: boolean;
  cliContextSource?: string;
}): void {
  if (input.selectionPersistent) {
    return;
  }
  input.presenter.line({
    code: "profile.selection.non_persistent",
    text: formatMessage(
      input.runtime.resolvedView,
      "profile.selection.non_persistent"
    ),
    data: {
      persistent: false,
      ...(input.cliContextSource
        ? { cliContextSource: input.cliContextSource }
        : {}),
    },
  });
}

async function normalizeCreateLocalAgentSelectionInput(input: {
  profileKind: CreateProfileKind;
  agentController?: string;
  agentControllerConversationId?: string;
  controllerEnabled?: boolean;
}): Promise<{
  agentControllerRef: string;
  agentControllerConversationId?: string;
  controllerEnabled?: boolean;
} | null> {
  const normalized = normalizeAgentControllerArgumentInput({
    agentController: input.agentController,
    agentControllerConversationId: input.agentControllerConversationId,
    controllerEnabled: input.controllerEnabled,
  });
  if (!normalized.hasAnyAgentControllerInput) {
    return null;
  }

  if (input.profileKind !== "agent") {
    throw new Error("--agent-controller can only be used with --kind agent");
  }

  const agentController = normalized.agentController;
  if (!agentController) {
    throw new Error(
      "--agent-controller-conversation-id requires --agent-controller."
    );
  }

  const parsed = parseAgentControllerRef(agentController);
  if (!parsed) {
    throw new Error("--agent-controller must use builtin:<id> or custom:<id>");
  }

  if (parsed.kind === "builtin") {
    await assertBuiltinAgentControllerRefRunnable(parsed.ref);
  }

  return {
    agentControllerRef: parsed.ref,
    ...(normalized.agentControllerConversationId
      ? {
          agentControllerConversationId:
            normalized.agentControllerConversationId,
        }
      : {}),
    ...(normalized.hasControllerEnabled
      ? { controllerEnabled: normalized.controllerEnabled }
      : {}),
  };
}

async function resolveTargetProfile(input: {
  explicitProfile: string | undefined;
  action: ProfileAction;
  allowPrompt: boolean;
  resolvedView: "human" | "agent";
  cachedProfiles?: WakeLoopProfile[];
  guideExamples?: ProfileActionGuideExamples;
}): Promise<string | null> {
  const fromInput = String(input.explicitProfile ?? "").trim();
  if (fromInput) {
    return fromInput;
  }

  const profiles =
    input.cachedProfiles ??
    (await listVisibleProfilesForListByView(input.resolvedView));
  const current = await resolveCurrentProfileId({
    profiles,
    mutateSelection: input.allowPrompt,
  });
  if (profiles.length === 0) {
    if (input.resolvedView === "agent") {
      throw createProfileActionGuideError(
        input.action,
        "no profiles found; create one after signing in.",
        input.guideExamples
      );
    }
    throw new Error(formatMessage(input.resolvedView, "profile.no_profiles"));
  }

  if (!input.allowPrompt) {
    if (input.resolvedView === "agent") {
      throw createProfileActionGuideError(
        input.action,
        "explicit profile argument is required in agent view.",
        input.guideExamples
      );
    }
    throw new Error(
      formatMessage(input.resolvedView, "profile.target.missing", {
        action: input.action,
      })
    );
  }

  if (input.resolvedView === "human") {
    const selected = await promptSearchSelect({
      message: `Choose a profile to ${input.action}`,
      items: profiles.map((profile) =>
        buildProfileSearchItem({
          profile,
          current,
          resolvedView: input.resolvedView,
        })
      ),
      maxVisible: 8,
      allowBack: true,
      backLabel: "Exit",
    });
    if (selected.status === "cancelled") {
      cancel("Cancelled.");
      return null;
    }
    if (selected.status === "back") {
      return null;
    }
    return selected.value;
  }

  const selected = await select({
    message: `Choose a profile to ${input.action}`,
    options: [
      ...profiles.map((profile) => ({
        value: profile.wakeLoopProfileId,
        label: resolveProfileSelectLabel({
          profile,
          current,
          resolvedView: input.resolvedView,
        }),
        hint: resolveProfileSelectHint({
          profile,
          resolvedView: input.resolvedView,
        }),
      })),
      { value: EXIT_PROFILE_SELECTION, label: "Exit" },
    ],
  });
  if (isCancel(selected)) {
    cancel("Cancelled.");
    return null;
  }
  if (selected === EXIT_PROFILE_SELECTION) {
    return null;
  }
  return selected;
}

async function resolveTargetProfilesForDelete(input: {
  explicitProfile: string | undefined;
  allowPrompt: boolean;
  resolvedView: "human" | "agent";
  cachedProfiles?: WakeLoopProfile[];
  presenter: ReturnType<typeof createPresenter>;
}): Promise<string[] | null> {
  const fromInput = String(input.explicitProfile ?? "").trim();
  if (fromInput) {
    return [fromInput];
  }

  const profiles =
    input.cachedProfiles ??
    (await listVisibleProfilesForListByView(input.resolvedView));
  if (profiles.length === 0) {
    if (input.resolvedView === "agent") {
      throw createProfileActionGuideError(
        "delete",
        "no profiles found; create one after signing in."
      );
    }
    throw new Error(formatMessage(input.resolvedView, "profile.no_profiles"));
  }

  if (!input.allowPrompt) {
    if (input.resolvedView === "agent") {
      throw createProfileActionGuideError(
        "delete",
        "explicit profile argument is required in agent view."
      );
    }
    throw new Error(
      formatMessage(input.resolvedView, "profile.target.missing", {
        action: "delete",
      })
    );
  }

  const current = await resolveCurrentProfileId({
    profiles,
    mutateSelection: input.allowPrompt,
  });
  const deletableProfiles = profiles.filter(
    (profile) => !isProtectedDefaultHumanProfile(profile)
  );
  const protectedProfiles = profiles.filter((profile) =>
    isProtectedDefaultHumanProfile(profile)
  );
  if (deletableProfiles.length === 0) {
    throw new Error(
      "no deletable profiles: default human profile is protected"
    );
  }
  if (protectedProfiles.length > 0 && input.resolvedView === "human") {
    const protectedNames = protectedProfiles
      .map((profile) => normalizeProfileDisplayName(profile))
      .join(", ");
    input.presenter.line({
      code: "profile.delete.protected_default",
      text: pc.dim(
        `Protected: default profile cannot be deleted (${protectedNames}).`
      ),
    });
  }
  if (input.resolvedView === "human") {
    return promptHumanProfileDeleteSelection({
      profiles: deletableProfiles,
      current,
      resolvedView: input.resolvedView,
    });
  }

  const selected = await multiselect({
    message: "Choose profile(s) to delete",
    options: deletableProfiles.map((profile) => ({
      value: profile.wakeLoopProfileId,
      label: resolveProfileSelectLabel({
        profile,
        current,
        resolvedView: input.resolvedView,
      }),
      hint: resolveProfileDeleteHint({
        profile,
        resolvedView: input.resolvedView,
      }),
    })),
    required: true,
  });
  if (isCancel(selected)) {
    cancel("Cancelled.");
    return null;
  }

  const normalized = normalizeDeleteProfileSelection(selected);
  return normalized.length > 0 ? normalized : null;
}

async function promptHumanProfileDeleteSelection(input: {
  profiles: WakeLoopProfile[];
  current: string | null;
  resolvedView: "human";
}): Promise<string[] | null> {
  const selected = await promptSearchMultiselect({
    message: "Choose profile(s) to delete",
    items: input.profiles.map((profile) =>
      buildProfileSearchItem({
        profile,
        current: input.current,
        resolvedView: input.resolvedView,
        detail: resolveProfileDeleteHint({
          profile,
          resolvedView: input.resolvedView,
        }),
      })
    ),
    required: true,
    maxVisible: 8,
    allowBack: true,
    backLabel: "Exit",
  });
  if (selected.status === "cancelled") {
    cancel("Cancelled.");
    return null;
  }
  if (selected.status === "back") {
    return null;
  }
  const normalized = normalizeDeleteProfileSelection(selected.value);
  return normalized.length > 0 ? normalized : null;
}

async function listVisibleProfilesForListByView(
  resolvedView: "human" | "agent"
): Promise<WakeLoopProfile[]> {
  const profiles = await listWakeLoopProfiles();

  if (resolvedView === "human") {
    return profiles;
  }

  return profiles.filter((profile) =>
    isProfileKindAllowedInView({
      resolvedView,
      profileKind: profile.profileKind,
    })
  );
}

function assertProfileAllowedInView(input: {
  profileId: string;
  profileKind: WakeLoopProfile["profileKind"];
  resolvedView: "human" | "agent";
  action: ProfileAction;
}): void {
  if (input.resolvedView !== "agent") {
    return;
  }

  if (
    input.profileKind === "human" &&
    (input.action === "delete" || input.action === "update")
  ) {
    throw createProfileActionGuideError(
      input.action,
      `profile "${input.profileId}" is human; agent view can select or inspect human profiles, but human profile changes require human view.`
    );
  }

  if (
    isProfileKindAllowedInView({
      resolvedView: input.resolvedView,
      profileKind: input.profileKind,
    })
  ) {
    return;
  }

  throw createProfileActionGuideError(
    input.action,
    `profile "${input.profileId}" is ${input.profileKind}; agent view supports human and agent profiles only.`
  );
}

async function confirmDelete(input: {
  profiles: string[];
  force: boolean | undefined;
  allowPrompt: boolean;
  resolvedView: "human" | "agent";
}): Promise<boolean> {
  if (input.force) {
    return true;
  }
  if (!input.allowPrompt) {
    throw new Error(
      formatMessage(input.resolvedView, "profile.delete.force_required")
    );
  }

  const message =
    input.profiles.length === 1
      ? `Delete profile "${input.profiles[0]}"?`
      : `Delete ${input.profiles.length} profiles?`;
  const ok = await confirm({
    message,
    initialValue: false,
  });
  if (isCancel(ok)) {
    cancel("Cancelled.");
    return false;
  }
  return ok;
}

function normalizeDeleteProfileSelection(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const ids: string[] = [];
  for (const item of value) {
    const profileId = String(item ?? "").trim();
    if (!profileId || ids.includes(profileId)) {
      continue;
    }
    ids.push(profileId);
  }
  return ids;
}

async function resolveCurrentProfileId(input: {
  profiles: WakeLoopProfile[];
  mutateSelection: boolean;
}): Promise<string | null> {
  const { mutateSelection, profiles } = input;
  const selected = await getSelectedWakeLoopProfileId();
  if (
    selected &&
    profiles.some((item) => item.wakeLoopProfileId === selected)
  ) {
    return selected;
  }

  if (selected && mutateSelection) {
    await clearSelectedWakeLoopProfileId();
  }

  const fallbackHuman =
    profiles.find(
      (item) => item.profileKind === "human" && item.isDefault === true
    ) ?? profiles.find((item) => item.profileKind === "human");
  if (fallbackHuman) {
    if (mutateSelection) {
      await setSelectedWakeLoopProfileId(fallbackHuman.wakeLoopProfileId);
    }
    return fallbackHuman.wakeLoopProfileId;
  }

  const fallback = profiles[0]?.wakeLoopProfileId ?? null;
  if (fallback && mutateSelection) {
    await setSelectedWakeLoopProfileId(fallback);
  }
  return fallback;
}

async function resolveSelectedProfileIdForList(
  profiles: WakeLoopProfile[]
): Promise<string | null> {
  const selected = await getSelectedWakeLoopProfileId();
  if (!selected) {
    return null;
  }
  return profiles.some((item) => item.wakeLoopProfileId === selected)
    ? selected
    : null;
}

function buildProfileSearchItem(input: {
  profile: WakeLoopProfile;
  current: string | null;
  resolvedView: "human" | "agent";
  detail?: string;
}): {
  value: string;
  label: string;
  detail: string;
  searchText: string;
} {
  return {
    value: input.profile.wakeLoopProfileId,
    label: resolveProfileSelectLabel({
      profile: input.profile,
      current: input.current,
      resolvedView: input.resolvedView,
    }),
    detail:
      input.detail ??
      resolveProfileSelectHint({
        profile: input.profile,
        resolvedView: input.resolvedView,
      }),
    searchText: buildProfileSearchText(input.profile),
  };
}

function buildProfileSearchText(profile: WakeLoopProfile): string {
  return [
    profile.wakeLoopProfileId,
    normalizeOptionalString(profile.profileName),
    profile.profileKind,
    normalizeOptionalString(profile.ownerUserId),
    normalizeOptionalString(profile.ownerName),
    normalizeOptionalString(profile.source),
  ]
    .filter((value): value is string => typeof value === "string")
    .join("\n");
}

function resolveProfileSelectLabel(input: {
  profile: WakeLoopProfile;
  current: string | null;
  resolvedView: "human" | "agent";
}): string {
  const displayName =
    input.resolvedView === "human"
      ? normalizeProfileDisplayName(input.profile)
      : input.profile.wakeLoopProfileId;
  if (input.profile.wakeLoopProfileId === input.current) {
    return `${displayName} (selected)`;
  }
  return displayName;
}

function resolveProfileSelectHint(input: {
  profile: WakeLoopProfile;
  resolvedView: "human" | "agent";
}): string {
  if (input.resolvedView === "human") {
    return `${input.profile.profileKind} · ${input.profile.wakeLoopProfileId}`;
  }
  return `${input.profile.profileKind} · ${normalizeProfileDisplayName(input.profile)}`;
}

function resolveProfileDeleteHint(input: {
  profile: WakeLoopProfile;
  resolvedView: "human" | "agent";
}): string {
  const base = resolveProfileSelectHint(input);
  if (!isProtectedDefaultHumanProfile(input.profile)) {
    return base;
  }
  return `${base} · default profile (cannot delete)`;
}

function formatProfileListLine(input: {
  profile: WakeLoopProfile;
  selected: boolean;
  resolvedView: "human" | "agent";
}): string {
  const mark = input.selected ? "*" : " ";
  const suffix = input.selected ? " (selected)" : "";
  if (input.resolvedView === "agent") {
    return `${mark} ${input.profile.wakeLoopProfileId}${suffix}`;
  }

  const displayName = normalizeProfileDisplayName(input.profile);
  return `${mark} ${displayName}${suffix} · ${input.profile.profileKind} · ${input.profile.status} · id: ${input.profile.wakeLoopProfileId}`;
}

function buildProfileListData(input: {
  profile: WakeLoopProfile;
  selected: boolean;
}): {
  profile: string;
  wakeLoopProfileId: string;
  profileName: string;
  profileKind: WakeLoopProfile["profileKind"];
  profileStatus: WakeLoopProfile["status"];
  status: WakeLoopProfile["status"];
  ownerUserId: string;
  ownerName?: string;
  source: string;
  isDefault: boolean;
  createdByUserId: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  selected: boolean;
  default: boolean;
} {
  const { profile, selected } = input;
  return {
    profile: profile.wakeLoopProfileId,
    wakeLoopProfileId: profile.wakeLoopProfileId,
    profileName: profile.profileName,
    profileKind: profile.profileKind,
    profileStatus: profile.status,
    status: profile.status,
    ownerUserId: profile.ownerUserId,
    ...(profile.ownerName ? { ownerName: profile.ownerName } : {}),
    source: profile.source,
    isDefault: profile.isDefault === true,
    createdByUserId: profile.createdByUserId,
    lastUsedAt: profile.lastUsedAt,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
    selected,
    default: profile.isDefault === true,
  };
}

function normalizeProfileDisplayName(profile: WakeLoopProfile): string {
  const name = String(profile.profileName ?? "").trim();
  if (name.length > 0) {
    return name;
  }
  return profile.wakeLoopProfileId;
}

function normalizeAgentControllerArgumentInput(input: {
  agentController?: string;
  agentControllerConversationId?: string;
  controllerEnabled?: boolean;
}): {
  agentController: string;
  agentControllerConversationId?: string;
  controllerEnabled?: boolean;
  hasControllerEnabled: boolean;
  hasAnyAgentControllerInput: boolean;
} {
  const agentController = String(input.agentController ?? "").trim();
  const agentControllerConversationId = normalizeOptionalString(
    input.agentControllerConversationId
  );
  const hasControllerEnabled = typeof input.controllerEnabled === "boolean";
  const hasAnyAgentControllerInput =
    Boolean(agentController) ||
    Boolean(agentControllerConversationId) ||
    hasControllerEnabled;

  return {
    agentController,
    ...(agentControllerConversationId ? { agentControllerConversationId } : {}),
    ...(hasControllerEnabled
      ? { controllerEnabled: input.controllerEnabled }
      : {}),
    hasControllerEnabled,
    hasAnyAgentControllerInput,
  };
}

function resolveUpdatableFields(profile: WakeLoopProfile): string[] {
  if (profile.profileKind === "agent") {
    return [
      "Profile name",
      "Agent controller",
      "Agent Controller Permission Mode",
      "Workspace",
    ];
  }
  return ["Profile name"];
}

function isProtectedDefaultHumanProfile(profile: WakeLoopProfile): boolean {
  return profile.profileKind === "human" && profile.isDefault === true;
}

function assertProfileDeletable(profile: WakeLoopProfile): void {
  if (!isProtectedDefaultHumanProfile(profile)) {
    return;
  }
  throw new Error("default human profile is protected and cannot be deleted");
}

function renderProfileReadyCard(input: {
  presenter: ReturnType<typeof createPresenter>;
  profile: WakeLoopProfile;
  state: "created" | "selected" | "updated";
  agentRuntimeState: Awaited<
    ReturnType<typeof resolveAgentProfileRuntimeState>
  >;
}): void {
  let stateLabel = "Updated";
  if (input.state === "created") {
    stateLabel = "Created";
  } else if (input.state === "selected") {
    stateLabel = "Selected";
  }
  const ownerName = String(input.profile.ownerName ?? "").trim();
  const rows: Array<{ label: string; value: string }> = [
    { label: "Result", value: stateLabel },
    { label: "Selected Now", value: "Yes" },
    { label: "Profile Name", value: input.profile.profileName },
    { label: "Profile Handle", value: `@${input.profile.profileHandle}` },
    { label: "Type", value: input.profile.profileKind },
    { label: "Profile ID", value: input.profile.wakeLoopProfileId },
    { label: "Status", value: input.profile.status },
    ...(ownerName
      ? [
          {
            label: "Account Name",
            value: ownerName,
          },
        ]
      : []),
    { label: "Account ID", value: input.profile.ownerUserId },
  ];

  if (input.profile.isDefault === true) {
    rows.push({ label: "Default for This Account", value: "Yes" });
  }
  rows.push(...buildProfileCreateWakeReadinessRows(input));
  rows.push(...buildAgentProfileStateRows(input.profile));
  rows.push(...buildAgentProfileRuntimeRows(input.agentRuntimeState));

  renderInfoCard({
    presenter: input.presenter,
    title:
      input.state === "created" && input.profile.profileKind === "agent"
        ? "Agent Profile Identity Created"
        : "Profile Created",
    codePrefix: "profile.created.card",
    rows,
    sanitize: true,
  });
}

function renderProfileSummaryCard(input: {
  presenter: ReturnType<typeof createPresenter>;
  profile: WakeLoopProfile;
  agentRuntimeState: Awaited<
    ReturnType<typeof resolveAgentProfileRuntimeState>
  >;
}): void {
  const ownerName = String(input.profile.ownerName ?? "").trim();
  const rows: Array<{ label: string; value: string }> = [
    { label: "Profile Name", value: input.profile.profileName },
    { label: "Profile Handle", value: `@${input.profile.profileHandle}` },
    { label: "Type", value: input.profile.profileKind },
    { label: "Profile ID", value: input.profile.wakeLoopProfileId },
    { label: "Status", value: input.profile.status },
    ...(ownerName
      ? [
          {
            label: "Account Name",
            value: ownerName,
          },
        ]
      : []),
    { label: "Account ID", value: input.profile.ownerUserId },
  ];

  if (input.profile.isDefault === true) {
    rows.push({ label: "Default for This Account", value: "Yes" });
  }
  rows.push(...buildAgentProfileStateRows(input.profile));
  rows.push(...buildAgentProfileRuntimeRows(input.agentRuntimeState));

  renderInfoCard({
    presenter: input.presenter,
    title: "Profile",
    codePrefix: "profile.show.card",
    rows,
    sanitize: true,
  });
}

function buildAgentCreateNextSteps(profile: WakeLoopProfile): string[] {
  if (profile.profileKind === "agent") {
    return [
      formatWakeLoopCliCommand(
        `wakeloop space add-members <space-id> --member ${profile.wakeLoopProfileId} --profile <human-profile-id> --view agent`
      ),
    ];
  }

  return [
    formatWakeLoopCliCommand("wakeloop profiles list --view agent"),
    formatWakeLoopCliCommand(
      `wakeloop profiles set ${profile.wakeLoopProfileId} --view agent`
    ),
    formatWakeLoopCliCommand(
      `wakeloop whoami --profile ${profile.wakeLoopProfileId} --view agent`
    ),
    formatWakeLoopCliCommand(
      `wakeloop space create --profile ${profile.wakeLoopProfileId} --name <space-name> --view agent`
    ),
    formatWakeLoopCliCommand(
      `wakeloop space join --profile ${profile.wakeLoopProfileId} <space-id> --view agent`
    ),
  ];
}

function buildAgentCreateWakeReadinessData(
  profile: WakeLoopProfile
): Record<string, unknown> {
  if (profile.profileKind !== "agent") {
    return {};
  }
  return {
    identityCreated: true,
    wakeReadinessVerified: false,
    wakeReadinessNote:
      "Profile creation creates the Space identity. Add it to a Space when the user wants this teammate to participate.",
  };
}

function buildProfileCreateWakeReadinessRows(input: {
  profile: WakeLoopProfile;
  state: "created" | "selected" | "updated";
}): Array<{ label: string; value: string }> {
  if (input.state !== "created" || input.profile.profileKind !== "agent") {
    return [];
  }
  return [
    {
      label: "Space identity",
      value: "Created",
    },
    {
      label: "Next",
      value:
        "Add this profile to a Space when the user wants this teammate to participate.",
    },
  ];
}

function buildAgentUpdateNextSteps(profile: WakeLoopProfile): string[] {
  if (profile.profileKind === "agent") {
    return [
      formatWakeLoopCliCommand(
        `wakeloop profiles show ${profile.wakeLoopProfileId} --view agent`
      ),
      formatWakeLoopCliCommand(
        `wakeloop space add-members <space-id> --member ${profile.wakeLoopProfileId} --profile <human-profile-id> --view agent`
      ),
    ];
  }

  return [
    formatWakeLoopCliCommand(
      `wakeloop profiles show ${profile.wakeLoopProfileId} --view agent`
    ),
    formatWakeLoopCliCommand(
      `wakeloop profiles update ${profile.wakeLoopProfileId} --name <new-name> --view agent`
    ),
    formatWakeLoopCliCommand("wakeloop profiles list --view agent"),
  ];
}

async function applyAgentProfileWorkspace(input: {
  profile: WakeLoopProfile;
  workspaceMode?: "wakeloop_managed" | "custom";
  customWorkspacePath?: string;
}): Promise<void> {
  if (!input.workspaceMode) {
    return;
  }
  if (input.profile.profileKind !== "agent") {
    throw new Error("Workspace can only be configured for agent profiles.");
  }
  if (input.workspaceMode === "wakeloop_managed") {
    await setWakeLoopManagedProfileWorkspace({
      wakeLoopProfileId: input.profile.wakeLoopProfileId,
    });
    return;
  }
  if (!input.customWorkspacePath) {
    throw new Error(
      "--workspace-path is required when --workspace-mode custom is used."
    );
  }
  await setCustomProfileWorkspace({
    wakeLoopProfileId: input.profile.wakeLoopProfileId,
    customWorkspacePath: input.customWorkspacePath,
  });
}

async function listBindableBuiltinControllers(): Promise<
  Array<AgentTargetState & { targetKind: "builtin" }>
> {
  const states = await listAgentTargetStates();
  return resolveBindableBuiltinControllerCandidates(states);
}

function resolveBindableBuiltinControllerCandidates(
  states: readonly AgentTargetState[]
): Array<AgentTargetState & { targetKind: "builtin" }> {
  return states
    .filter(isBindableBuiltinControllerCandidate)
    .sort((left, right) => left.agentId.localeCompare(right.agentId));
}

function isBindableBuiltinControllerCandidate(
  state: AgentTargetState
): state is AgentTargetState & { targetKind: "builtin" } {
  return (
    state.targetKind === "builtin" &&
    state.detected &&
    state.lifecycleStatus !== "suspended" &&
    state.runtimeSupported &&
    state.selectable &&
    state.launchStatus === "ready" &&
    state.launchContract !== null
  );
}

async function resolveBindableBuiltinController(
  agentControllerRef: string
): Promise<AgentTargetState & { targetKind: "builtin" }> {
  const normalizedAgentControllerRef = String(agentControllerRef).trim();
  const localAgentId = resolveLocalRegistryAgentIdFromAgentControllerRef({
    agentControllerRef: normalizedAgentControllerRef,
  });
  const controllers = await listBindableBuiltinControllers();
  const matchedController =
    controllers.find((controller) => controller.agentId === localAgentId) ??
    null;
  if (matchedController) {
    return matchedController;
  }

  const bindableControllerIds = controllers.map(
    (controller) => controller.agentId
  );
  throw new Error(
    bindableControllerIds.length > 0
      ? `Built-in agent controller '${normalizedAgentControllerRef}' is not runnable. Available built-in agent controllers: ${bindableControllerIds.join(", ")}`
      : `Built-in agent controller '${normalizedAgentControllerRef}' is not runnable. No built-in agent controllers are currently runnable.`
  );
}

async function assertBuiltinAgentControllerRefRunnable(
  agentControllerRef: string
): Promise<void> {
  await resolveBindableBuiltinController(agentControllerRef);
}

function resolveProfileUpdateCommandExamples(): ProfileUpdateCommandExamples {
  return {};
}

function toProfileActionGuideExamples(
  _input: ProfileUpdateCommandExamples
): ProfileActionGuideExamples {
  return {};
}

function normalizeOptionalString(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeWorkspaceModeInput(
  value: unknown
): "wakeloop_managed" | "custom" | null {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replaceAll("-", "_");
  if (!normalized) {
    return null;
  }
  if (normalized === "wakeloop_managed" || normalized === "custom") {
    return normalized;
  }
  throw new Error("--workspace-mode must be wakeloop-managed|custom");
}

function normalizeModelSelectionInput(input: {
  model?: string;
  reasoningEffort?: string;
}): {
  hasAnyModelSelectionInput: boolean;
  modelId?: string;
  reasoningLevel?: string;
} {
  const modelId = normalizeOptionalString(input.model);
  const reasoningLevel = normalizeOptionalString(input.reasoningEffort);
  if (!(modelId || reasoningLevel)) {
    return {
      hasAnyModelSelectionInput: false,
    };
  }
  if (!modelId) {
    throw new Error("--reasoning-effort requires --model.");
  }
  return {
    hasAnyModelSelectionInput: true,
    modelId,
    ...(reasoningLevel ? { reasoningLevel } : {}),
  };
}

async function ensureProfileCommandAuthenticationOrCancel(input: {
  action: ProfileCommandAuthAction;
  runtime: RuntimeContext;
}): Promise<boolean> {
  if (input.runtime.resolvedView !== "human") {
    return true;
  }

  const evaluated = await evaluateCommandAuthentication();
  if (evaluated.status === "valid" && evaluated.session) {
    primeWakeLoopProfilesSessionCache(evaluated.session);
    return true;
  }

  const authenticated = await ensureCommandAuthenticationOrCancel({
    runtime: input.runtime,
    promptMessage: resolveProfileAuthPromptMessage(input.action),
    allowPrompt: canUseInteractivePrompts(input.runtime),
    evaluated,
  });
  if (authenticated) {
    const refreshed = await evaluateCommandAuthentication();
    if (refreshed.status === "valid" && refreshed.session) {
      primeWakeLoopProfilesSessionCache(refreshed.session);
    }
  }
  return authenticated;
}

function resolveProfileAuthPromptMessage(
  action: ProfileCommandAuthAction
): string {
  switch (action) {
    case "create":
      return buildCommandAuthenticationPromptMessage("creating a profile");
    case "list":
      return buildCommandAuthenticationPromptMessage("viewing your profiles");
    case "show":
      return buildCommandAuthenticationPromptMessage("showing a profile");
    case "set":
      return buildCommandAuthenticationPromptMessage("choosing a profile");
    case "update":
      return buildCommandAuthenticationPromptMessage("updating a profile");
    case "delete":
      return buildCommandAuthenticationPromptMessage("deleting a profile");
    default:
      return buildCommandAuthenticationPromptMessage();
  }
}
